import { modelManager } from '../manager/entityManager';
import { globalStores, TGlobalStores } from '../stores';
import { EFaceType } from '../entities/type';
import { ESeparateWallType } from '../data/separateWall/SeparateWall';
import { EFaceDataBaseType, EWallFaceLayout } from '../data/face/face';
import { getBelongRegionColumns } from '../stores/FaceStore/utils';
import { Curve2dUtils } from '../math/Curve2dUtils';
import { IMaterialInfo } from '../data/material/material';
import { EOperationType } from '../types/operation';
import { isMaterialNotReplaceable } from '../utils/face';
import { getPathFromEdges } from '../utils/edge';
import { dispersePath } from '../utils/shape';

export class FaceOperation {
  private _faceStore: TGlobalStores['faceStore'];
  private _history: TGlobalStores['history'];

  constructor(gs: TGlobalStores) {
    this._faceStore = gs.faceStore;
    this._history = gs.history;
  }

  private $history_modifyFaceMaterials(datas: { id: string; materialInfo: IMaterialInfo }[]) {
    this._history.startOperation();

    datas.forEach((data) => {
      this._faceStore.updateFaceMaterial(data.id, data.materialInfo);
    });

    // 将操作记录压栈
    this._history.completeOperation(EOperationType.FaceModify);
  }

  modifyFaceMaterial(faceId: string, materialId: string) {
    const faceData = this._faceStore.getFaceData(faceId);
    if (!faceData) return;

    // 玻璃隔墙和半玻隔墙中间段不更新材质
    if (faceData.relativeInfo.sepType === ESeparateWallType.GlassWall) return false;
    if (
      faceData.relativeInfo.sepType === ESeparateWallType.HalfGlassWall &&
      faceData.relativeInfo.layout === EWallFaceLayout.MIDDLE
    )
      return;

    this.$history_modifyFaceMaterials([{ id: faceId, materialInfo: { id: materialId } }]);

    modelManager.faceManager.updateEntity(faceId);

    return true;
  }

  applyToSameRegion(faceId: string) {
    const faceData = this._faceStore.getFaceData(faceId);
    if (!faceData || (faceData.type !== EFaceType.SepWallFace && faceData.type !== EFaceType.ColumnFace)) return;

    let regionId = faceData.relativeInfo.regionId;
    const columnId = faceData.relativeInfo.columnId;
    const columnData = columnId ? globalStores.structStore.getColumnData(columnId) : undefined;
    if (!regionId && columnData) {
      // 通过柱子获取分区id
      const regions = globalStores.regionStore.getRegionDatas();
      for (const region of regions) {
        const path = dispersePath(getPathFromEdges(region.pathInfos)); //region.pathInfos.map((info) => info.start);
        const holes = region.holeInfos.map((holes) => dispersePath(getPathFromEdges(holes))); //holes.map((hole) => hole.start));
        if (Curve2dUtils.isPointInPolygon(columnData.center, path)) {
          let inHole = false;

          for (const hole of holes) {
            if (Curve2dUtils.isPointInPolygon(columnData.center, hole)) {
              inHole = true;
              break;
            }
          }

          if (!inHole) {
            regionId = region.id;
            break;
          }
        }
      }
    }
    if (!regionId) return;

    // 分区数据缺失说明 regionId 已失效，整个操作以分区为范围，此时不做部分更新
    const regionData = globalStores.regionStore.getRegionData(regionId);
    if (!regionData) return;

    const updateDatas: { id: string; materialInfo: IMaterialInfo }[] = [];
    const sepWallFaceDatas = globalStores.faceStore.getSepWallFaceDatas();
    sepWallFaceDatas.forEach((sepWallFaceData) => {
      if (isMaterialNotReplaceable(sepWallFaceData)) return;

      if (sepWallFaceData.relativeInfo.regionId === regionId) {
        updateDatas.push({ id: sepWallFaceData.id, materialInfo: faceData.materialInfo });
      }
    });

    const columnIds = new Set(getBelongRegionColumns([regionData]).map((column) => column.id));
    const columnFaceDatas = globalStores.faceStore.getColumnFaceDatas();
    columnFaceDatas.forEach((columnFaceData) => {
      if (columnFaceData.baseDataType === EFaceDataBaseType.Contour) return;
      if (!columnFaceData.relativeInfo.columnId) return;

      if (columnIds.has(columnFaceData.relativeInfo.columnId)) {
        updateDatas.push({ id: columnFaceData.id, materialInfo: faceData.materialInfo });
      }
    });

    this.$history_modifyFaceMaterials(updateDatas);
    modelManager.faceManager.update();
  }
}
